import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import { recordBehaviorEvent } from '../../util/api';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

// brand×category-path index — which brands have listings under each category path.
// Powers dynamic brand-filter narrowing. Regenerate with build_brand_category_index.py.
import brandCategoryIndex from '../../config/brandCategoryIndex.json';
import filterCoverageIndex from '../../config/filterCoverageIndex.json';

// ─── Matterax coverage-gate ────────────────────────────────────────────────
// Hide a category-specific filter whose value coverage in the ACTIVE L2 is
// below `filterCoverageIndex.threshold`, so clicking an under-tagged filter can
// never collapse a category to a near-empty result page (which would hurt
// browsability more than having no filter at all).
//
// FULLY REVERSIBLE: set this flag to `false` and the search page renders the
// EXACT original filter set — the gate below becomes a no-op. (Regenerate the
// coverage numbers with build_filter_coverage_index.py as tagging improves;
// filters auto-reveal once their category crosses the threshold.)
const FILTER_COVERAGE_GATE_ENABLED = true;

import { H3, H5, IconSpinner, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';
import { fetchPreviewCount } from './SearchPage.duck';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy, { RANDOM_SORT_KEY } from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import SearchSubNav from './SearchSubNav/SearchSubNav';
import SidebarToggles from './SidebarToggles/SidebarToggles';
import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

/**
 * Sort listings by colour proximity when a colourway filter is active.
 *
 * The pub_colour_family param value is ordered by proximity to the picked colour
 * e.g. "has_any:deep-blue-navy,light-blue,teal-petrol" — nearest key first.
 * Listings whose colour_family contains the nearest key rank highest.
 */
function sortListingsByColourProximity(listings, searchParams) {
  const colourParam = searchParams?.pub_colour_family;
  if (!colourParam || !colourParam.startsWith('has_any:')) return listings;

  const orderedKeys = colourParam.replace('has_any:', '').split(',');
  if (orderedKeys.length <= 1) return listings;

  const score = listing => {
    const colourFamily = listing.attributes?.publicData?.colour_family;
    if (!Array.isArray(colourFamily) || colourFamily.length === 0) return orderedKeys.length;
    // Find the best (lowest) rank among the listing's colour keys
    let best = orderedKeys.length;
    for (const key of colourFamily) {
      const idx = orderedKeys.indexOf(key);
      if (idx !== -1 && idx < best) best = idx;
    }
    return best;
  };

  return [...listings].sort((a, b) => score(a) - score(b));
}

/**
 * Sink computer-generated paint colour CHIPS (flat dummyimage.com swatches —
 * Jotun/Dulux/Nippon tinting colours) below real photographed materials on
 * KEYWORD result pages. Their evocative names ("Rustic", "Misty") outrank the
 * true materials a texture/mood query means. Mirrors the server-side demotion
 * in server/api/hybridSearch.js — this covers the paths the server can't
 * reorder (keyword-native Sharetribe ranking, concierge colour filters).
 * Stable partition per page; skipped when the query itself is about paint
 * (same intent regex as the server) or when there is no keyword query at all
 * (category browsing of a paint brand must keep its natural order).
 */
function demoteColourChips(listings, searchParams) {
  const query = searchParams?.keywords || searchParams?.ai_query || '';
  if (!query) return listings;
  const paintIntent =
    /\b(paints?|painted|coatings?|emulsions?|enamel|primer|undercoat|sheen|tinting|colou?r(s|way|ways|\s*code)?|jotun|dulux|nippon|ral|ncs|lrv)\b/i.test(
      query
    ) || /\b\d{3,}\b/.test(query);
  if (paintIntent) return listings;
  const isChip = l =>
    /dummyimage\.com/.test(l.attributes?.publicData?.image_primary || '');
  const real = listings.filter(l => !isChip(l));
  if (real.length === 0 || real.length === listings.length) return listings;
  return [...real, ...listings.filter(isChip)];
}

/* Randomise now runs server-side — SearchPage.duck sweeps all matching
 * listing IDs, shuffles with the URL seed, slices the current page, and
 * fetches only those listings. The duck respects the existing redux flow
 * so this component just renders whatever is in state. The client-side
 * shuffleListingsBySeed helper that used to live here was removed when
 * cross-page random was wired up — see project_taxonomy_l3_delta.md for
 * the deploy SOP. */

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const SCROLL_STORAGE_KEY = 'searchPage:lastScrollY';

/**
 * Log one filter interaction into the event_log warehouse. `changed` is the
 * param diff exactly as the filter UI produced it (null/undefined value =
 * cleared); current keywords + category ride along so "which facets get used
 * inside stone vs paint" and "filtering while searching vs browsing" are one
 * GROUP BY away. Best-effort — filtering must never notice its own logging.
 */
const logFilterChange = (changed, urlQueryParams) => {
  try {
    const entries = Object.entries(changed || {});
    if (entries.length === 0) return;
    const changedOut = {};
    entries.forEach(([k, v]) => {
      changedOut[k] =
        v == null
          ? null
          : (typeof v === 'object' ? JSON.stringify(v) : String(v)).slice(0, 200);
    });
    const category = ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3']
      .map(k => urlQueryParams?.[k])
      .filter(Boolean)
      .join('>');
    recordBehaviorEvent('filter_change', {
      context: {
        changed: changedOut,
        ...(urlQueryParams?.keywords
          ? { keywords: String(urlQueryParams.keywords).slice(0, 120) }
          : {}),
        ...(category ? { category } : {}),
      },
    });
  } catch (e) {
    // never break filtering on analytics
  }
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      mobilePreviewCount: null,
    };
    this._previewTimer = null;
    this._scrollSaveTimer = null;
    // Scroll we want to restore on mount (e.g. user came back from listing
    // page). Read from sessionStorage once; skipped if empty/0.
    this._pendingScrollRestore = null;

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.handleScrollSave = this.handleScrollSave.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.onApplyMobileFilters = this.onApplyMobileFilters.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleRandomise = this.handleRandomise.bind(this);
    this.maybeApplyDefaultShuffle = this.maybeApplyDefaultShuffle.bind(this);
    this.maybeWidenEmptyScopedSearch = this.maybeWidenEmptyScopedSearch.bind(this);
  }

  // ── Zero-result auto-widen ────────────────────────────────────────────────
  // A typed search carries the browsed category scope ("search within" —
  // see carriedSearchParams in TopbarSearchForm). When the query's results
  // all live OUTSIDE that scope (searching "calacatta" from inside
  // Lighting), the intersection is empty and the page dead-ends on
  // "No results" even though the catalogue has plenty. Amazon-style
  // recovery: drop the category scope, re-run once, and say so
  // (?searched_all=<dropped category id> renders the notice). The marker
  // param doubles as the loop guard — a widened search that is STILL empty
  // just shows the normal no-results state.
  maybeWidenEmptyScopedSearch() {
    const {
      listings = [],
      searchInProgress,
      searchListingsError,
      searchParams = {},
      location,
      history,
      routeConfiguration,
    } = this.props;
    if (searchInProgress || searchListingsError || listings.length > 0) return;
    const sp = parse(location.search);
    if (sp.searched_all) return; // widened already — don't loop
    const hasQuery = !!(sp.keywords || sp.ids);
    // Deepest-first so the notice names the most specific scope dropped.
    const scopeKeys = ['pub_categoryLevel3', 'pub_categoryLevel2', 'pub_categoryLevel1'].filter(
      k => sp[k]
    );
    if (!hasQuery || scopeKeys.length === 0) return;
    // Only act when the search that just finished (empty) is THIS URL's
    // search — never on a stale response from a previous location.
    const fingerprint = p =>
      ['keywords', 'ids', 'pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3']
        .map(k => `${k}:${p[k] || ''}`)
        .join('|');
    if (fingerprint(searchParams) !== fingerprint(sp)) return;
    const widened = { ...sp };
    for (const k of ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3', 'page']) {
      delete widened[k];
    }
    widened.searched_all = sp[scopeKeys[0]];
    // replace, not push: the empty scoped page is worthless as history — Back
    // should return to the browse the user searched from.
    history.replace(
      createResourceLocatorString('SearchPage', routeConfiguration, {}, widened)
    );
  }

  componentDidMount() {
    if (typeof window === 'undefined') return;

    // Matterax: RANDOMISE-BY-DEFAULT. A plain catalogue browse should open in
    // the stratified brand-capped shuffle, not newest-first (which clumps the
    // most-recently bulk-uploaded brand at the top). This injects a fresh
    // ?random=<seed> on landing, reusing the exact client-side sweep the
    // Shuffle button uses — so the expensive all-IDs sweep stays off the SSR
    // critical path. No-op once a seed / explicit sort / keyword search is
    // present (filters + pagination preserve the seed), so it never re-fires
    // against the user's choice.
    this.maybeApplyDefaultShuffle();

    // If we're returning from a listing page, restore the scroll position the
    // user left. Kept as a flag so componentDidUpdate can apply it AFTER the
    // search finishes (otherwise the scroll-to-top below overrides us).
    try {
      const saved = parseInt(sessionStorage.getItem(SCROLL_STORAGE_KEY), 10);
      if (saved > 0) {
        this._pendingScrollRestore = saved;
      }
    } catch (e) {
      // ignore storage errors
    }

    window.addEventListener('scroll', this.handleScrollSave, { passive: true });
  }

  componentWillUnmount() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('scroll', this.handleScrollSave);
    if (this._scrollSaveTimer) clearTimeout(this._scrollSaveTimer);
  }

  // Persist scroll position (throttled) so we can restore it on back-navigation.
  handleScrollSave() {
    if (this._scrollSaveTimer) return;
    this._scrollSaveTimer = setTimeout(() => {
      this._scrollSaveTimer = null;
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
      } catch (e) {
        // ignore
      }
    }, 150);
  }

  componentDidUpdate(prevProps) {
    // ── Re-sync state.currentQueryParams from URL on external nav.
    //
    // Bug fixed May 2026: clicking a colour swatch from `/s` with an
    // L2 category active (e.g. paint-finish) was jumping the page to
    // a different L2 (stone), because:
    //   1. User navigates via SearchSubNav from stone → paint-finish.
    //      Only history.push runs; component stays mounted, state
    //      keeps the OLD stone categoryLevel params.
    //   2. User clicks Red. getHandleChangedValueFn merges with
    //      `{ ...urlQueryParams (paint-finish), ...prevState (stone) }`
    //      — state OVERRIDES the new URL. Result: pushes stone back.
    //
    // Fix: when location.search changes externally, reset state to
    // the new URL params. The internal filter-click path that mutates
    // state stays unchanged.
    if (prevProps.location?.search !== this.props.location?.search) {
      this.setState({
        currentQueryParams: validUrlQueryParamsFromProps(this.props),
      });
      // Matterax: re-assert randomise-by-default when we arrive at a fresh
      // unsorted view via client-side nav (Topbar → catalogue, cleared search,
      // etc.). No-op when a seed/sort/keyword is already present — filter
      // apply + pagination preserve the seed — so it never fights the user or
      // double-fires.
      this.maybeApplyDefaultShuffle();
    }

    // When a search completes (initial load OR filter change)…
    if (prevProps.searchInProgress && !this.props.searchInProgress) {
      // A scoped keyword/ids search that found nothing widens itself to all
      // categories (with a notice) instead of dead-ending — see the method.
      this.maybeWidenEmptyScopedSearch();
      if (this._pendingScrollRestore != null) {
        // …we just came back from a listing page — restore the prior position.
        const y = this._pendingScrollRestore;
        this._pendingScrollRestore = null;
        // One tick gives the DOM a moment to render card heights.
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      } else {
        // …a normal filter/query change — scroll to top.
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true, mobilePreviewCount: null });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false, mobilePreviewCount: null });
  }

  // Fire a debounced lightweight search (perPage:1) to get a live count
  // for the mobile "Show X items" button while filters are staged.
  scheduleMobilePreviewFetch() {
    if (this._previewTimer) clearTimeout(this._previewTimer);
    this._previewTimer = setTimeout(() => {
      const { onFetchPreviewCount, config } = this.props;
      onFetchPreviewCount(this.state.currentQueryParams, config).then(count => {
        if (count != null) this.setState({ mobilePreviewCount: count });
      });
    }, 300);
  }

  // Reset all filter query parameters
  resetAll(e) {
    logFilterChange({ reset_all: true }, validUrlQueryParamsFromProps(this.props));
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {}, mobilePreviewCount: null });

    // Reset routing params
    const baseQueryParams = omit(urlQueryParams, filterQueryParamNames);
    // Matterax: keep the randomise-by-default seed after a reset so the cleared
    // catalogue stays shuffled (rather than snapping back to newest-first).
    const sortKey = config?.search?.sortConfig?.queryParamName || 'sort';
    const rawSeed = parse(location.search).random;
    const queryParams =
      rawSeed && !baseQueryParams[sortKey]
        ? { ...baseQueryParams, random: rawSeed }
        : baseQueryParams;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      // Every filter UI (desktop accordions, mobile sheet, swatches, toggles,
      // colourway picker) funnels its param diff through here — the one choke
      // point where "which filter did the user just touch" is explicit.
      logFilterChange(updatedURLParams, urlQueryParams);
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Defense-in-depth (May 2026 fix): category-level params are
        // NAVIGATION CONTEXT (set by SearchSubNav clicks, route
        // changes, etc.) — not filter state. URL is the source of
        // truth. Without this override, a stale prevState containing
        // an OLD L1/L2/L3 could leak back into the next history.push
        // when an unrelated filter is clicked, causing the page to
        // jump from /s?L1=materials → /s?L1=materials&L2=stone, etc.
        for (const k of ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3']) {
          if (urlQueryParams[k]) {
            mergedQueryParams[k] = urlQueryParams[k];
          } else {
            delete mergedQueryParams[k];
          }
        }

        // Brand is a CROSS-CATEGORY facet. Selecting a brand shows that
        // brand's FULL catalogue, so it CLEARS the category navigation
        // context — otherwise brand ∩ category is empty for any brand not
        // in the active L1 (Nippon/Dulux/Interface are Materials → 0 under
        // the FF&E tab), which is exactly why the brand filter looked
        // broken. brand_slug's searchAcrossAllCategories:true keeps the
        // param valid once the category is gone. Mirrors the working
        // ProfilePage brand-chip behaviour (/s?pub_brand_slug=…, no L1).
        if (updatedURLParams['pub_brand_slug']) {
          delete mergedQueryParams['pub_categoryLevel1'];
          delete mergedQueryParams['pub_categoryLevel2'];
          delete mergedQueryParams['pub_categoryLevel3'];
        }

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          // Matterax: carry the randomise-by-default seed through a filter
          // change (validUrlQueryParams strips `random`, so it's re-read from
          // the raw URL). Without this, applying a filter would briefly show
          // newest-first then swap to the shuffle — two fetches + a flash.
          // A chosen sort clears the seed, so guard on that.
          const sortKey = sortConfig?.queryParamName || 'sort';
          const rawSeed = parse(location.search).random;
          const searchWithSeed =
            rawSeed && !search[sortKey] ? { ...search, random: rawSeed } : search;

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, searchWithSeed)
          );
        } else {
          // Mobile: filters are staged — fire a preview count fetch so the
          // "Show X items" button reflects the pending filter selection.
          this.scheduleMobilePreviewFetch();
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    // Matterax: "Shuffled" is a display key, not a Sharetribe sort param.
    // Picking it means "back to the default order" — reshuffle instead of
    // sending __shuffled__ to the API.
    if (values === RANDOM_SORT_KEY) {
      this.handleRandomise();
      return;
    }
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    logFilterChange({ [urlParam || 'sort']: values || null }, urlQueryParams);

    // Choosing a sort wins — clear any active randomise seed
    const cleared = omit(urlQueryParams, 'random');
    const queryParams = values
      ? { ...cleared, [urlParam]: values }
      : omit(cleared, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Push a fresh random seed into the URL. Each click reshuffles the entire
  // matching-set across pages (duck does the sweep + shuffle + slice).
  //
  // Preserves the user's CURRENT PAGE — random mode just reshuffles the
  // catalogue, the page index stays. Strips any active sort so the two modes
  // stay mutually exclusive at the URL level (the duck would ignore sort in
  // random mode anyway; this just keeps the URL clean).
  //
  // Reads the raw URL via parse(location.search) instead of the filtered
  // validUrlQueryParamsFromProps so the `random` key itself + unknown params
  // survive the round trip.
  handleRandomise() {
    const { history, routeConfiguration, location, config } = this.props;
    const rawQueryParams = parse(location.search);
    const sortKey = config?.search?.sortConfig?.queryParamName || 'sort';
    const preserved = omit(rawQueryParams, [sortKey, 'random']);
    // Math.random is fine for a non-cryptographic shuffle seed; we just need
    // a fresh integer that's different from the previous one. Scale to 1e9
    // for visually distinct seeds in the URL.
    const newSeed = Math.floor(Math.random() * 1e9) + 1;
    const queryParams = { ...preserved, random: String(newSeed) };
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Matterax: randomise-by-default trigger. Fired on mount + on external nav
  // into a fresh view. Injects a seed only when the browse is genuinely
  // "unsorted": no explicit sort, no active shuffle seed, and not a
  // relevance-ranked search (keyword `keywords` / hybrid-vector `ids`, whose
  // order is meaningful — shuffling would bury the best matches). Uses
  // history.replace so the default shuffle doesn't add a back-button entry.
  //
  // SCOPE: the AUTO shuffle covers the Materials category AND the default
  // catalogue view (bare /s — no L1 — which is materials-dominated), so the
  // landing isn't dominated by the newest bulk-uploaded brand. It is SKIPPED
  // only on the explicit FF&E and Architectural tabs, which keep their default
  // (newest) order. The manual Shuffle button (handleRandomise) is unaffected
  // and still works in every category.
  maybeApplyDefaultShuffle() {
    if (typeof window === 'undefined') return;
    const { history, routeConfiguration, location, config } = this.props;
    if (!config?.search?.sortConfig?.active) return;
    const raw = parse(location.search);
    const sortKey = config?.search?.sortConfig?.queryParamName || 'sort';
    const hasSort = raw[sortKey] != null && `${raw[sortKey]}` !== '';
    const hasSeed = raw.random != null && /^\d+$/.test(`${raw.random}`);
    const isRelevanceRanked = raw.keywords != null || raw.ids != null;
    const l1 = raw.pub_categoryLevel1;
    const isExcludedCategory = l1 === 'ffe' || l1 === 'architectural';
    if (hasSort || hasSeed || isRelevanceRanked || isExcludedCategory) return;
    const seed = Math.floor(Math.random() * 1e9) + 1;
    const queryParams = { ...raw, random: String(seed) };
    // Default catalogue view (bare /s) → Materials only. FF&E and Architectural
    // are opt-in tabs; the mixed all-categories view is never the default.
    // EXCEPT brand-scoped entries (/s?pub_brand_slug=x from the Brands
    // directory): the brand's full cross-category catalog is the point, and
    // forcing Materials would empty the page for FF&E-only brands.
    if (!l1 && !raw.pub_brand_slug) queryParams.pub_categoryLevel1 = 'materials';
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.replace(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Apply accumulated mobile filter state to URL (called when "Show listings" is tapped)
  onApplyMobileFilters() {
    this.getHandleChangedValueFn(true)({});
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFiltersRaw = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // DYNAMIC BRAND NARROWING — the brand filter (brand_slug) lists every brand
    // across all categories. Once a category is chosen, intersect its options with
    // the brands that ACTUALLY have listings in that category path (materials:stone
    // → 9 stone brands; …:stone:natural-stone → 3), so the dropdown narrows as the
    // user drills L1 → L2 → L3. No category selected ⇒ no narrowing (show all).
    const categoryPath = [
      validQueryParams.pub_categoryLevel1,
      validQueryParams.pub_categoryLevel2,
      validQueryParams.pub_categoryLevel3,
    ]
      .filter(Boolean)
      .join(':');
    const brandsForCategory = brandCategoryIndex.byPath[categoryPath];
    const brandNarrowedFilters =
      brandsForCategory && brandsForCategory.length
        ? availableFiltersRaw.map(f =>
            f.key === 'brand_slug' && Array.isArray(f.enumOptions)
              ? { ...f, enumOptions: f.enumOptions.filter(o => brandsForCategory.includes(o.option)) }
              : f
          )
        : availableFiltersRaw;

    // Coverage-gate (see FILTER_COVERAGE_GATE_ENABLED above): within the ACTIVE
    // L2, drop any category facet whose value coverage is below threshold so a
    // click can't collapse the category to a near-empty page. Flag OFF ⇒ this
    // is a pure pass-through and the original filter set renders unchanged.
    const activeL2 = validQueryParams.pub_categoryLevel2;
    const coverageForL2 =
      FILTER_COVERAGE_GATE_ENABLED && activeL2 ? filterCoverageIndex.byL2?.[activeL2] : null;
    const availableFilters = coverageForL2
      ? brandNarrowedFilters.filter(f => {
          const cov = coverageForL2[f.key];
          return !(typeof cov === 'number' && cov < filterCoverageIndex.threshold);
        })
      : brandNarrowedFilters;

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    // ids= count:
    //  - Paginated (totalPages > 1, e.g. Cosentino's 70 ids at 30/page):
    //    show pagination.totalItems — the full set total the user pages
    //    through. The ids are pre-filtered visible (brand/author
    //    short-circuit), so totalItems matches what renders.
    //  - Single page (totalPages <= 1): show listings.length, the
    //    post-hidden-strip count, so a set that included some
    //    hidden_from_search ids still reads "N shown" not "N passed"
    //    (the original "11 results / 4 shown" fix).
    // Non-ids searches always use pagination.totalItems.
    const isIdsSearch = !!searchParams?.ids;
    const idsPaginated = isIdsSearch && pagination && pagination.totalPages > 1;
    const totalItems = isIdsSearch
      ? idsPaginated
        ? pagination.totalItems
        : listings.length
      : searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    // Randomise-by-default: while the default shuffle is being applied (URL about
    // to gain ?random) OR its shuffled results are still loading, show a spinner
    // instead of the un-shuffled (newest) grid — so the landing never flashes the
    // GANI-newest order before reshuffling. Stateless (derived from the URL), so
    // it can never get stuck. SSR renders this spinner too (no full-opacity flash
    // on hydration). Scoped to the default shuffle: excluded tabs, explicit
    // sort/keyword, and everything after the shuffle loads render the grid.
    const rawForShuffle = parse(location.search);
    const l1ForShuffle = rawForShuffle.pub_categoryLevel1;
    const shuffleContext =
      sortConfig.active &&
      l1ForShuffle !== 'ffe' &&
      l1ForShuffle !== 'architectural' &&
      !(rawForShuffle[sortConfig.queryParamName] != null && `${rawForShuffle[sortConfig.queryParamName]}` !== '') &&
      rawForShuffle.keywords == null &&
      rawForShuffle.ids == null;
    const hasShuffleSeed = rawForShuffle.random != null && /^\d+$/.test(`${rawForShuffle.random}`);
    // Where the shuffle APPLIES — same scope as shuffleContext but without the
    // "no explicit sort" clause, so the Shuffled option stays offered (and
    // reachable) after the user has picked a real sort.
    const isShuffleAvailable =
      sortConfig.active &&
      l1ForShuffle !== 'ffe' &&
      l1ForShuffle !== 'architectural' &&
      rawForShuffle.keywords == null &&
      rawForShuffle.ids == null;
    // Pending redirect (no seed yet) → spinner; seed present but results not in yet → spinner.
    const showShuffleSpinner = shuffleContext && (!hasShuffleSeed || !listingsAreLoaded);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isRandomActive={/^\d+$/.test(String(parse(location.search).random || ''))}
          isShuffleAvailable={isShuffleAvailable}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };

    // Inline (non-popup) sort for the mobile bottom sheet — styled like FilterPlain
    const sortByForSheet = sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isRandomActive={hasShuffleSeed}
        isShuffleAvailable={isShuffleAvailable}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={this.handleSortBy}
        showAsPopup={false}
        mode="mobile"
        filterStyle={true}
      />
    ) : null;
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
        currentUser={currentUser}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // Search notices ("Did you mean", auto-correction, widened search).
    // Desktop shows them in the left slot of the Sort / Randomise row so
    // they line up with the controls; mobile (where that row is hidden)
    // renders them on their own line above the grid.
    const searchNotice = (() => {
      // "Showing results for …" (synonym/spell auto-correction)
      // and "Did you mean …?" (sparse-result typo suggestion).
      // Both ride in as URL params set by TopbarSearchForm from
      // the hybrid-search response. Plain anchors re-run search.
      const sp = parse(location.search);
      // The search widened an over-tight reading rather than
      // returning nothing — say which constraint went, so results
      // that don't match what was typed are explained instead of
      // looking like the search ignored the request.
      const relaxedRaw = sp.relaxed;
      const RELAX_LABEL = {
        keywords: 'the exact wording',
        L3: 'the sub-category',
        mood: 'the style',
        colour: 'the colour',
        visual_effect: 'the look',
      };
      const relaxedLabels = relaxedRaw
        ? String(relaxedRaw)
            .split(',')
            .map(r => RELAX_LABEL[r])
            .filter(Boolean)
        : [];
      const correctedFor = sp.corrected_for;
      const didYouMean = sp.did_you_mean;
      const aiIntro = sp.ai_intro;
      const shown = sp.keywords;
      // Zero-result auto-widen marker (maybeWidenEmptyScopedSearch):
      // the scoped search found nothing, so the category scope was
      // dropped — name it, or results that ignore the browsed
      // category look like a search bug.
      const searchedAll = sp.searched_all;
      const findCatName = (nodes, id) => {
        for (const n of nodes || []) {
          if (n.id === id) return n.name;
          const deep = findCatName(n.subcategories, id);
          if (deep) return deep;
        }
        return null;
      };
      const searchedAllLabel = searchedAll
        ? findCatName(listingCategories, searchedAll) ||
          String(searchedAll)
            .replace(/-/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase())
        : null;
      // The AI-search theme (ai_query + its colour filter) still carries in the
      // URL, but renders no banner (user decision 2026-08-11) — exiting the
      // theme happens via any SearchSubNav navigation, which drops ai_query.
      // ai_intro is dropped once you leave the first results, so the curated
      // blurb only shows there.
      if (!correctedFor && !didYouMean && !aiIntro && !relaxedLabels.length && !searchedAll)
        return null;
      const linkStyle = { color: '#052439', fontWeight: 600, textDecoration: 'underline' };
      // Re-run a suggested query IN-APP. These were raw <a href>s,
      // so every "did you mean" cost a full page reload AND threw
      // away the browsed category and every active filter — the
      // correction fixed the spelling and undid the search.
      const runSuggestedQuery = term => e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        const keep = { ...sp };
        for (const k of ['keywords', 'ids', 'page', 'corrected_for', 'did_you_mean', 'ai_query', 'ai_intro', 'relaxed', 'searched_all']) {
          delete keep[k];
        }
        this.props.history.push(
          createResourceLocatorString('SearchPage', this.props.routeConfiguration, {}, {
            ...keep,
            keywords: term,
          })
        );
      };
      const wrapStyle = { fontSize: 14, color: '#5a6472' };
      const aiStyle = {
        padding: '8px 12px', margin: '8px 0 4px', fontSize: 14, color: '#052439',
        background: '#f4f9e6', border: '1px solid #e0fa51', borderRadius: 8,
        display: 'inline-block',
      };
      return (
        <div style={wrapStyle}>
          {aiIntro ? (
            <div style={aiStyle}>
              <span role="img" aria-label="AI">✨</span>{' '}
              <strong>Curated for you:</strong> {aiIntro}
            </div>
          ) : null}
          {searchedAllLabel ? (
            <span>
              No matches in <strong>{searchedAllLabel}</strong>, so showing results from
              all categories.
            </span>
          ) : null}
          {relaxedLabels.length ? (
            <span>
              Nothing matched exactly, so we widened{' '}
              <strong>{relaxedLabels.join(' and ')}</strong>.
            </span>
          ) : null}
          {correctedFor ? (
            <span>
              Showing results for <strong>{shown}</strong>. Search instead for{' '}
              <a style={linkStyle} href={`/s?keywords=${encodeURIComponent(correctedFor)}`} onClick={runSuggestedQuery(correctedFor)}>
                {correctedFor}
              </a>
              .
            </span>
          ) : null}
          {didYouMean ? (
            <span>
              Did you mean{' '}
              <a style={linkStyle} href={`/s?keywords=${encodeURIComponent(didYouMean)}`} onClick={runSuggestedQuery(didYouMean)}>
                {didYouMean}
              </a>
              ?
            </span>
          ) : null}
        </div>
      );
    })();

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <SearchSubNav
          validQueryParams={validQueryParams}
          config={config}
          totalItems={totalItems}
          listingsAreLoaded={listingsAreLoaded}
          routeConfiguration={routeConfiguration}
          history={this.props.history}
        />
        <div className={css.layoutWrapperContainer}>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              <SidebarToggles
                validQueryParams={validQueryParams}
                routeConfiguration={routeConfiguration}
                history={this.props.history}
              />
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersDesktop"
                    className={css.filter}
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside>

          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByInSheet={sortByForSheet}
                onRandomise={sortConfig.active ? this.handleRandomise : null}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={this.state.mobilePreviewCount != null ? this.state.mobilePreviewCount : totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
                onApplyFilters={this.onApplyMobileFilters}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;

                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersMobile"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit={false}
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                onRandomise={this.handleRandomise}
                summary={searchNotice}
              />
              {searchNotice ? (
                <div className={css.searchNoticeMobile}>{searchNotice}</div>
              ) : null}
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]:
                    !showShuffleSpinner && !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                {showShuffleSpinner ? (
                  <div className={css.shuffleLoading}>
                    <IconSpinner />
                  </div>
                ) : (
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={demoteColourChips(
                      sortListingsByColourProximity(listings, parse(location.search)),
                      parse(location.search)
                    )}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    listingTypeParam={listingTypePathParam}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const allListings = getListingsById(state, currentPageResultIds);
  // Matterax: hide listings flagged publicData.hidden_from_search === true
  // from the catalogue grid. Used today by ~50 Cosentino imports whose
  // hero images couldn't be sourced from cosentino.com (Earthic Raw,
  // Le Chic, Pietra Kode lines without consumer color pages). They
  // remain visible on the supplier's public ProfilePage (which queries
  // by author_id and doesn't apply this filter) and at /l/<id> direct
  // links, just not in the discovery grid where a thumbnail-less card
  // would look broken. Generic flag — any future hide-from-search use
  // case can set the same boolean.
  const listings = (allListings || []).filter(
    l => l?.attributes?.publicData?.hidden_from_search !== true
  );

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchPreviewCount: (params, config) => dispatch(fetchPreviewCount(params, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
